import React, { useMemo } from "react"
import { Map, Marker, GoogleApiWrapper } from "google-maps-react"
import PropTypes from "prop-types"

const LoadingContainer = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ height: "250px" }}>
        <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading map...</span>
        </div>
    </div>
)

const LocationMapDisplay = ({
    google,
    location,
    height = "250px",
    zoom = 10,
    className = "",
    style = {},
    showControls = false,
    appInfo = null,
}) => {
    const mapCenter = useMemo(() => {
        if (location?.posLat && location?.posLong) {
            const lat = parseFloat(location.posLat)
            const lng = parseFloat(location.posLong)
            
            // Validate that we have valid coordinates
            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                return { lat, lng }
            }
        }
        return null
    }, [location])

    // Don't render if no location
    if (!mapCenter) {
        return null
    }

    const mapStyles = {
        width: "100%",
        height: height,
        position: "relative",
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid #e9ecef",
        ...style
    }

    const googleMapStyles = [
        {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
        },
        {
            featureType: "transit",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
        }
    ]

    return (
        <div className={`location-map-display ${className}`} style={mapStyles}>
            <Map
                google={google}
                zoom={zoom}
                style={{ 
                    width: "100%", 
                    height: "100%",
                    borderRadius: "8px"
                }}
                center={mapCenter}
                initialCenter={mapCenter}
                disableDefaultUI={!showControls}
                zoomControl={showControls}
                mapTypeControl={false}
                scaleControl={false}
                streetViewControl={false}
                rotateControl={false}
                fullscreenControl={false}
                styles={googleMapStyles}
                gestureHandling={showControls ? "auto" : "none"}
                clickableIcons={false}
                onReady={(_, map) => {
                    if (map && mapCenter) {

                        map.setCenter(mapCenter)
                        map.setZoom(zoom)
                    }
                }}
            >
                <Marker
                    position={mapCenter}
                    icon={{
                        url: "data:image/svg+xml;base64," + btoa(`
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
                                <path fill="#4a6cf7" stroke="#ffffff" stroke-width="2" d="M16 2C9.373 2 4 7.373 4 14c0 8.5 12 22 12 22s12-13.5 12-22c0-6.627-5.373-12-12-12z"/>
                                <circle fill="#ffffff" cx="16" cy="14" r="4"/>
                            </svg>
                        `),
                        scaledSize: new window.google.maps.Size(32, 40),
                        anchor: new window.google.maps.Point(16, 40)
                    }}
                />
            </Map>
        </div>
    )
}

LocationMapDisplay.propTypes = {
    google: PropTypes.object.isRequired,
    location: PropTypes.shape({
        posLat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        posLong: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    height: PropTypes.string,
    zoom: PropTypes.number,
    className: PropTypes.string,
    style: PropTypes.object,
    showControls: PropTypes.bool,
    appInfo: PropTypes.object,
}

export default GoogleApiWrapper({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    LoadingContainer: LoadingContainer
})(LocationMapDisplay)